<template>
    <section class="test-drive">
        <div class="confirm-details__section container">
            <StepBar :step="5" step-name="Confirm your details" />
            <Navigation :go-to-step="{ name: 'select-time', params: {brand: brand, urlModel: this.urlModel}}" />

            <div class="test-drive__content">
                <div class="confirm-details__content">
                    <h2 class="title">Please confirm your details</h2>

                    <div class="card">
                        <form @submit.prevent="submit" ref="form" class="card__inner">
                            <div class="form-grid form-grid--xs-1-column">
                                <div class="form-grid__col-span-2 form-grid__col--xs-full-width">
                                    <title-input />
                                </div>
                                <div class="form-grid__col form-grid__col--xs-full-width">
                                    <first-name />
                                </div>
                                <div class="form-grid__col form-grid__col--xs-full-width">
                                    <last-name />
                                </div>
                                <div class="form-grid__col form-grid__col--xs-full-width">
                                    <email />
                                </div>
                                <div class="form-grid__col form-grid__col--xs-full-width">
                                    <telephone :inputNullable="false" inputValidateMessage="Please enter a telephone number" />
                                </div>
                                <div class="form-grid__col-span-2 form-grid__col--xs-full-width">
                                    <privacy />
                                </div>
                            </div>
                        </form>
                    </div>

                    <button type="button" @click="submit" class="btn btn--primary select-datetime__content--continue">Continue</button>
                </div>

                <div class="confirm-details__info">
                    <picture v-if="model.image">
                        <source v-if="model.image_webp" :srcset="model.image_webp" type="image/webp" />
                        <img class="img-responsive confirm-details__content--image" :src="model.image" :alt="model.model_title" />
                    </picture>

                    <div class="confirm-details__content--details">
                        <h3 class="capitalise">{{ model.model_title }}</h3>
                        <p class="extras">
                            <span>Engine:</span> {{ fuelChoice }} <br />
                            <span>Transmission:</span> {{ transmissionChoice.toLowerCase() }}
                        </p>

                        <p v-if="notes !== ''">
                            <span>Notes:</span> <br />
                            {{ notes }}
                        </p>
                    </div>

                    <div class="confirm-details__content--fix-spacing">
                        <Navigation :go-to-step="{ name: 'select-model', params: {brand: brand}}" link-text="Change model" />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import StepBar from "../components/StepBar";
import Navigation from "../components/Navigation";
import { mapState } from "vuex";
import { format } from "date-fns";
import TitleInput from "../../core/components/Forms/Title.vue";
import FirstName from "../../core/components/Forms/FirstName.vue";
import LastName from "../../core/components/Forms/LastName.vue";
import Email from "../../core/components/Forms/Email.vue";
import Telephone from "../../core/components/Forms/Telephone.vue";
import Privacy from '../../core/components/Forms/Privacy.vue';
import { customEvent, generateLeadEvent } from '../../modules/tracking/AnalyticsEvents';


export default {
    props: {
        brand: {
            required: true,
        },
        urlModel: {
            required: true,
        },
    },
    components: {
        StepBar,
        Navigation,
        TitleInput,
        FirstName,
        LastName,
        Email,
        Telephone,
        Privacy
    },
    data() {
        return {
            acceptedTermsAndConditions: false,
            validator: false,
        };
    },
    created() {
        if (!this.haveMinimumModelData) {
            this.$router.push({ name: 'select-options', params: {brand: brand, urlModel: this.urlModel}});
            return;
        }
        if (!this.haveMinimumDealerData) {
            this.$router.push({ name: 'select-dealer', params: {brand: brand, urlModel: this.urlModel}});
            return;
        }
        if (!this.haveMinimumTimeData) {
            this.$router.push({ name: 'select-time', params: {brand: brand, urlModel: this.urlModel}});
            return;
        }
    },
    mounted() {
        this.validator = new ContactFormValidator({
            element: $(this.$refs.form),
            vueForm: true,
        });

        if (this.step5Page === true) {
            customEvent("New Car - Test Drive Form", "Revisit", "Step 5 Page");
        } else {
            customEvent("New Car - Test Drive Form", "Visited", "Step 5 Page");
            this.$store.commit("setStep5Page", true);
        }
    },
    methods: {
        async submit() {
            if (!await this.validator.isValid()) {
                return;
            }

            const formData = new FormData(this.$refs.form);
            formData.set("brand", this.brand);
            formData.set("fuelChoice", this.fuelChoice);
            formData.set("transmissionChoice", this.transmissionChoice);
            formData.set("notes", this.notes);
            try {
                formData.set("date", format(this.date, "ddd Do MMM YYYY"));
            } catch (error) {
                console.error(error);
            }
            formData.set("time", this.time);
            formData.set("location", this.customerAddress);
            formData.set("location-id", this.dealer.id);

            this.validator.preventInput();

            axios
                .post("/api/test-drive/" + this.model.id, formData)
                .then(response => {
                    this.validator.allowInput();
                    customEvent("New Car - Test Drive Form", "Submit", "Complete");
                    generateLeadEvent('new_test_drive');
                    this.$store.commit("setDealerDirectionsLink", response.data.directions_link);
                    this.$router.push({ name: 'thank-you', params: {brand: this.brand, urlModel: this.urlModel}});
                })
                .catch((error) => {
                    console.error(error);
                    this.validator.errorResponse(error);
                });
        },
    },
    computed: {
        ...mapState(["model", "fuelChoice", "transmissionChoice", "notes", "dealer", "customerAddress", "date", "time", "step5Page"]),

        haveMinimumModelData() {
            if (this.fuelChoice == '') {
                return false;
            }
            if (this.transmissionChoice == '') {
                return false;
            }
            return true;
        },

        haveMinimumDealerData() {
            if (this.dealer.length == 0) {
                return false;
            }
            return true;
        },

        haveMinimumTimeData() {
            if (this.date == '') {
                return false;
            }
            if (this.time == '') {
                return false;
            }
            return true;
        },


        title: {
            get() {
                return this.$store.state.title;
            },
            set(value) {
                this.$store.commit("setTitle", value);
            },
        },
        firstName: {
            get() {
                return this.$store.state.firstName;
            },
            set(value) {
                this.$store.commit("setFirstName", value);
            },
        },
        lastName: {
            get() {
                return this.$store.state.lastName;
            },
            set(value) {
                this.$store.commit("setLastName", value);
            },
        },
        email: {
            get() {
                return this.$store.state.email;
            },
            set(value) {
                this.$store.commit("setEmail", value);
            },
        },
        phone: {
            get() {
                return this.$store.state.phone;
            },
            set(value) {
                this.$store.commit("setPhone", value);
            },
        },
        contactMethod: {
            get() {
                return this.$store.state.contactMethod;
            },
            set(value) {
                this.$store.commit("setContactMethod", value);
            },
        },
    },
};
</script>


// WEBPACK FOOTER //
// resources/assets/js/test-drive/pages/step-5.vue